import { d as debounce } from './debounce-CFtxQv9K.js';

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.buttons = this.querySelectorAll('button');
    this.min = parseInt(this.input.min);
    this.max = parseInt(this.input.max);
    this.moq = parseInt(this.dataset.moq) || 1;
    this.remainingQtyUntilMoq = parseInt(this.dataset.remainingQtyUntilMoq || '0');

    // Optional: feste, erlaubte Mengen (Staffel-Stufen im Warenkorb).
    this.allowed = null;
    try {
      const parsedAllowed = JSON.parse(this.dataset.allowedQuantities || 'null');
      if (Array.isArray(parsedAllowed) && parsedAllowed.length) {
        this.allowed = parsedAllowed.map(Number).sort((a, b) => a - b);
      }
    } catch (e) {}

    // Optional: Verpackungseinheit. Oberhalb der groessten Staffel wird in VPE-Schritten
    // weitergezaehlt; ohne hinterlegte VPE in 1er-Schritten.
    this.vpe = parseInt(this.dataset.vpe || '0') || 0;

    // Freie Zone unterhalb der kleinsten Staffel: Liegt die MOQ (normalisiert, min. 1) unter der
    // kleinsten Staffel, darf zwischen MOQ und kleinster Staffel in 1er-Schritten gewaehlt werden.
    // Ab der kleinsten Staffel springt der Stepper wie bisher zwischen den Stufen.
    this.freeMin = null;
    if (this.allowed && this.moq < this.allowed[0]) {
      this.freeMin = Math.max(this.moq, 1);
    }

    this.buttons.forEach((button) => {
      button.addEventListener('click', (event) => this.handleStep(event.currentTarget.name));
    });

    this.input.addEventListener('change', () => {
      this.constrainAndUpdateInput(this.input.value);
      this.publishEvents();
    });

    this.intent = this.dataset.intent;

    if (this.intent === 'quantity:change') {
      this.publishEvents();
      // Listen for cart updates to recalculate MOQ requirements
      this.unsubscribeCartUpdated = subscribe('cart:updated', ({ data }) =>
        this.updateMoqFromCart(data)
      );
    }
  }

  disconnectedCallback() {
    if (this.unsubscribeCartUpdated) {
      this.unsubscribeCartUpdated();
    }
  }

  /**
   * Update MOQ requirements based on current cart state
   * @param {Object} cart - Cart data from the cart:updated event
   */
  updateMoqFromCart(cart) {
    if (!cart) return;

    // Calculate total quantity in cart for this product
    const productId = this.dataset.productId;
    let cartQuantity = 0;

    cart.items.forEach((item) => {
      if (item.product_id.toString() === productId) {
        cartQuantity += item.quantity;
      }
    });

    // Recalculate remaining quantity until MOQ
    const remainingQty = Math.max(this.moq - cartQuantity, 0);
    this.remainingQtyUntilMoq = remainingQty;

    // Update min and input value if necessary
    if (remainingQty > 0) {
      this.input.min = remainingQty;
      this.input.value = remainingQty;
    } else {
      this.input.min = 1;
      if (parseInt(this.input.value) === 0) {
        this.input.value = 1;
      }
    }

    this.publishEvents();
  }

  /**
   * Find the index of the allowed quantity closest to a value
   * @param {number} value
   * @returns {number}
   */
  nearestAllowedIndex(value) {
    let bestIdx = 0;
    let bestDiff = Infinity;
    for (let i = 0; i < this.allowed.length; i++) {
      const diff = Math.abs(this.allowed[i] - value);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIdx = i;
      }
    }
    return bestIdx;
  }

  /**
   * Handle the step up or step down of the quantity
   * @param {string} type - The type of step to be taken
   */
  handleStep(type) {
    // Warenkorb/Drawer: Am unteren Mengen-Limit wird der Minus-Button zum Entfernen-Button
    // (Papierkorb-Icon aus quantity-input.liquid). Klick setzt die Menge auf 0 und entfernt
    // die Position, statt wirkungslos zu sein. Untergrenze: freeMin bzw. kleinste Staffel im
    // Staffel-Modus, sonst MOQ (normal = 1). Produktseiten (quantity:change) unveraendert.
    // Vor dem Entfernen den Undo-Toast anstossen (window.tsiRemoveUndo aus line-item.js).
    if (type === 'minus' && this.intent === 'cart:change') {
      const cur = parseInt(this.input.value);
      const bottom = this.allowed
        ? (this.freeMin !== null ? this.freeMin : this.allowed[0])
        : Math.max(this.moq, 1);
      if (!isNaN(cur) && cur <= bottom) {
        const lineItemEl = this.closest('line-item');
        if (lineItemEl && window.tsiRemoveUndo) window.tsiRemoveUndo(lineItemEl);
        this.input.value = 0;
        this.publishEvents();
        return;
      }
    }

    // Staffel-Modus: unterhalb der kleinsten Stufe (ohne MOQ) in 1er-Schritten,
    // zwischen den Stufen springen, oberhalb der groessten Stufe in VPE-Schritten
    // (ohne VPE in 1er-Schritten), jeweils ohne Obergrenze.
    if (this.allowed) {
      let cur = parseInt(this.input.value);
      const lo = this.allowed[0];
      const hi = this.allowed[this.allowed.length - 1];
      const minQty = this.freeMin !== null ? this.freeMin : lo;
      const stepAboveMax = this.vpe > 0 ? this.vpe : 1;
      if (isNaN(cur)) cur = minQty;
      let next = cur;

      if (type === 'plus') {
        if (cur >= hi) {
          next = cur + stepAboveMax;
        } else if (this.freeMin !== null && cur < lo) {
          next = cur + 1;
        } else {
          next = hi;
          for (let i = 0; i < this.allowed.length; i++) {
            if (this.allowed[i] > cur) { next = this.allowed[i]; break; }
          }
        }
      } else {
        if (cur > hi) {
          next = cur - stepAboveMax;
          if (next < hi) next = hi;
        } else if (cur > lo) {
          next = lo;
          for (let j = this.allowed.length - 1; j >= 0; j--) {
            if (this.allowed[j] < cur) { next = this.allowed[j]; break; }
          }
        } else {
          next = this.freeMin !== null ? Math.max(cur - 1, this.freeMin) : lo;
        }
      }

      this.input.value = next;
      if (next !== cur) {
        this.publishEvents();
      }
      return;
    }

    const value = parseInt(this.input.value);
    const step = parseInt(this.input.step) || 1;
    const newValue = type === 'minus' ? Math.max(value - step, 0) : value + step;
    const updatedValue = this.constrainAndUpdateInput(newValue);

    if (parseInt(updatedValue) !== parseInt(value)) {
      this.publishEvents();
    }
  }

  /**
   * Update the quantity in the cart
   */
  publishEvents = debounce(
    () => {
      switch (this.intent) {
        case 'cart:change':
          publish('cart:change', {
            line: this.dataset.itemIndex,
            quantity: this.input.value,
          });
          break;
        case 'quantity:change':
          // Produkt-Scope mitsenden: product-form uebernimmt nur Events des
          // eigenen Produkts (MOQ-Schutz gegen Ueberschreiben fremder Formulare).
          publish('quantity:change', {
            quantity: this.input.value,
            productId: this.dataset.productId,
          });
          break;
      }
    },
    this.dataset.debounce === 'true' ? 300 : 0
  );

  /**
   * Constrain and update the input.
   * @param {number} newValue - The new value.
   * @returns {number} The constrained value.
   */
  constrainAndUpdateInput(newValue) {
    // Staffel-Modus: unterhalb der kleinsten Stufe (ohne MOQ) sind 1er-Schritte frei,
    // dazwischen wird auf die naechste Stufe eingerastet. Oberhalb der groessten Stufe:
    // mit VPE auf das naechste VPE-Vielfache, ohne VPE frei in 1er-Schritten, ohne Obergrenze.
    if (this.allowed) {
      let v = parseInt(newValue);
      const lo = this.allowed[0];
      const hi = this.allowed[this.allowed.length - 1];
      const minQty = this.freeMin !== null ? this.freeMin : lo;
      if (isNaN(v)) {
        this.input.value = minQty;
        return this.input.value;
      }
      if (v <= minQty) {
        this.input.value = minQty;
      } else if (this.freeMin !== null && v < lo) {
        this.input.value = v;
      } else if (v <= hi) {
        this.input.value = this.allowed[this.nearestAllowedIndex(v)];
      } else if (this.vpe > 0) {
        let k = Math.round((v - hi) / this.vpe);
        if (k < 0) k = 0;
        this.input.value = hi + k * this.vpe;
      } else {
        this.input.value = v;
      }
      return this.input.value;
    }

    const numericValue = parseInt(newValue);
    this.input.value = numericValue;

    if (numericValue >= this.max) {
      window.toast.error(
        window.toast_translations.product.quantity_error_max.replace('{{ max }}', this.max)
      );
      this.input.value = this.max;
    } else if (numericValue > 0 && numericValue < this.remainingQtyUntilMoq) {
      const bumped = Math.max(this.remainingQtyUntilMoq, 1);
      this.input.value = bumped;
      if (bumped > 1) {
        window.toast.error(
          window.toast_translations?.product?.moq_bump.replace(
            '{{ moq }}',
            this.remainingQtyUntilMoq
          )
        );
      }
    } else if (numericValue <= this.min) {
      this.input.value = this.min;
    }

    return this.input.value;
  }
}

customElements.define('quantity-input', QuantityInput);